<template>
  <v-container fluid>
    <v-row align="center">
      <v-col>
        <v-text-field
          label="Name"
          v-model="name"
          @input="dispatchChange"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Description"
          v-model="description"
          @input="dispatchChange"
          :outlined="outlinedInputs"
          dense
          hide-details
        ></v-text-field>
      </v-col>
      <download-csv :data="csvData" delimiter=",">
        <v-btn small class="mr-1"
          ><v-icon left small>mdi-file-delimited</v-icon> Get CSV</v-btn
        >
      </download-csv>
    </v-row>
    <v-row>
      <scenario-participants-list
        v-model="scenario.participants"
        :itinerary-participants="itineraryParticipants"
        @add-participants="onAddParticipants"
        @add-participant="onAddParticipant"
        @remove-participant="onRemoveParticipant"
        :scenarios="scenarios"
      />
    </v-row>
    <v-row justify="end" align="center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn class="mr-3 mt-3" v-on="on" icon @click="onShowPaxUpdater">
            <v-icon dark>mdi-account</v-icon>
          </v-btn>
        </template>
        <span>Pax Updater</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col cols="9">
        <h5 class="subtitle-2">Itinerary Data</h5>
        <v-card outlined>
          <v-data-table
            :headers="sourceTableHeaders"
            :items="items"
            class="services-table"
            hide-default-footer
            :expanded.sync="expanded"
            disable-pagination
            @update:sort-by="onSortByUpdate"
            @update:sort-desc="onSortDirUpdate"
          >
            <template v-slot:item.adultSell="{ item }">{{
              item.adultSell | priceLabeler
            }}</template>
            <template v-slot:item.childSell="{ item }">{{
              item.childSell | priceLabeler
            }}</template>
            <template v-slot:item.infantSell="{ item }">{{
              item.infantSell | priceLabeler
            }}</template>

            <template v-slot:item.startDate="{ item }">{{
              item.startDate | formatDate
            }}</template>
            <template v-slot:item.ratePlan="{ item }">{{
              item.ratePlan
            }}</template>
            <template v-slot:item.supplierName="{ item }">
              <truncated-label
                v-model="item.supplierName"
                :width="14"
              ></truncated-label>
            </template>
            <template v-slot:item.isIncluded="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.isIncluded"
                hide-details
              ></v-checkbox>
            </template>
            <template v-slot:item.isOptional="{ item }">
              <v-checkbox
                disabled
                class="pa-0 ma-0"
                v-model="item.isOptional"
                hide-details
              ></v-checkbox>
            </template>
            <template v-slot:item.serviceName="{ item }">
              <truncated-label
                v-model="item.serviceName"
                :width="14"
              ></truncated-label>
              <v-btn
                text
                small
                icon
                v-if="item.extras && item.extras.length > 0"
                @click="expandExtras(item)"
              >
                <v-icon color="grey">mdi-star</v-icon>
              </v-btn>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <table class="services-table">
                  <tr v-for="extra in item.extras" :key="extra.title">
                    <td v-for="header in headers" :key="header.name">
                      <template v-if="header.name == 'serviceType'">
                        <v-icon>{{
                          supplierTypeIcon(item.serviceType)
                        }}</v-icon>
                      </template>
                      <template v-else-if="header.name == 'startDate'">{{
                        extra.startDate | formatDate
                      }}</template>
                      <template v-else-if="header.name == 'adultSell'">
                        {{ extra.adultSell | priceLabeler }}
                      </template>
                      <template v-else-if="header.name == 'childSell'">
                        {{ extra.childSell | priceLabeler }}
                      </template>
                      <template v-else-if="header.name == 'infantSell'">
                        {{ extra.infantSell | priceLabeler }}
                      </template>
                      <template v-else-if="header.name == 'serviceName'">
                        <v-icon color="yellow darken-2" small>mdi-star</v-icon>
                        {{ extra.name }}
                      </template>
                      <template v-else-if="header.name == 'isIncluded'">
                        <v-checkbox
                          disabled
                          class="pa-0 ma-0"
                          v-model="extra.isIncluded"
                          hide-details
                        ></v-checkbox>
                      </template>
                      <template v-else-if="header.name == 'isOptional'">
                        <v-checkbox
                          disabled
                          class="pa-0 ma-0"
                          v-model="extra.isOptional"
                          hide-details
                        ></v-checkbox>
                      </template>
                      <template v-else>{{ extra[header.value] }}</template>
                    </td>
                  </tr>
                </table>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="3">
        <h5 class="subtitle-2">Service Group</h5>
        <v-card outlined>
          <v-data-table
            :items="items"
            :headers="editableTableHeaders"
            class="scenario-table"
            hide-default-footer
            :expanded.sync="expanded"
            disable-pagination
          >
            <template v-slot:item.adults="{ item }">
              <v-text-field
                type="number"
                v-model="item.adults"
                @input="onPaxChange(item)"
                min="0"
                :max="item.itiAdults"
                class="pt-1 ma-0 short-input"
                single-line
              ></v-text-field>
            </template>
            <template v-slot:item.children="{ item }">
              <v-text-field
                type="number"
                single-line
                v-model="item.children"
                @input="onPaxChange(item)"
                min="0"
                :max="item.itiChildren"
                class="pt-1 ma-0 short-input"
              ></v-text-field>
            </template>
            <template v-slot:item.infants="{ item }">
              <v-text-field
                type="number"
                single-line
                v-model="item.infants"
                @input="onPaxChange(item)"
                min="0"
                :max="item.itiInfants"
                class="pt-1 ma-0 short-input"
              ></v-text-field>
            </template>
            <template v-slot:expanded-item="{ item }">
              <td :colspan="headers.length" class="pa-0">
                <table>
                  <tr v-for="extra in item.extras" :key="extra.title">
                    <td
                      v-for="header in editableTableHeaders"
                      :key="header.name"
                    >
                      <template v-if="header.name == 'adults'">
                        <v-text-field
                          type="number"
                          v-model="extra.adults"
                          @input="onPaxChange(extra)"
                          min="0"
                          :max="extra.itiAdults"
                          class="pt-1 ma-0 short-input"
                          :disabled="extra.isChildOnly || extra.isInfantOnly"
                          single-line
                        ></v-text-field>
                      </template>
                      <template v-else-if="header.name == 'children'">
                        <v-text-field
                          type="number"
                          single-line
                          v-model="extra.children"
                          @input="onPaxChange(extra)"
                          min="0"
                          :max="extra.itiChildren"
                          class="pt-1 ma-0 short-input"
                        ></v-text-field>
                      </template>
                      <template v-else>{{ extra[header.value] }}</template>
                    </td>
                  </tr>
                </table>
              </td>
            </template>
          </v-data-table>
        </v-card>
        <v-row>
          <v-col>
            <v-row justify="center">
              <strong
                >Total Service Group Price:
                {{ totalPrice | priceLabeler }}</strong
              >
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row justify="center">
          <v-btn color="warning" text @click="onRemove"
            >Remove Service Group</v-btn
          >
        </v-row>
      </v-col>
    </v-row>
    <service-type-pax-updater
      :show="showPaxUpdater"
      @close="showPaxUpdater = false"
      v-model="itineraryItems"
      @update="onPaxUpdate"
    />
    <v-row v-if="debug">
      <code>{{ sourceTableHeaders }}</code>
      <code>{{ editableTableHeaders }}</code>
      <h3>headers</h3>
      <code>{{ headers }}</code>
    </v-row>
  </v-container>
</template>

<script>
import { sortBy } from "../../../../helpers";
import TruncatedLabel from "../../shared/TruncatedLabel.vue";
import ScenarioParticipantsList from "./ScenarioParticipantsList.vue";
import ServiceTypePaxUpdater from "./ServiceTypePaxUpdater";
export default {
  name: "scenario",
  components: {
    ScenarioParticipantsList,
    ServiceTypePaxUpdater,
    TruncatedLabel,
  },
  props: {
    visibleColumns: {
      type: Array,
      default: () => [],
    },
    scenario: {
      type: Object,
      default: () => {},
    },
    itineraryItems: {
      type: Array,
      default: () => [],
    },
    itineraryParticipants: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Object,
      default: () => {},
    },
    scenarios: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      debug: false,
      name: this.scenario ? this.scenario.name : "New Service Group",
      description: this.scenario ? this.scenario.description : "",
      scenarioItems: this.scenario ? this.scenario.items : [],
      expanded: [],
      participants: this.scenario ? this.scenario.participants : [],
      showPaxUpdater: false,
      currentSort: "orderNo",
      sortDesc: false,
    };
  },

  computed: {
    csvData() {
      //const lines = this.items.map(this.itemToCSV);
      const lines = this.items.reduce((acc, item) => {
        const line = this.itemToCSV(item);
        acc.push(line);
        if (item.extras && item.extras.length) {
          item.extras.forEach((extra) => {
            const extraLine = this.itemToCSV(extra);
            acc.push(extraLine);
          });
        }
        return acc;
      }, []);
      return [
        ...lines,
        {
          Date: "-",
          Supplier: "-",
          Service: "-",
          Adults: "-",
          Children: "-",
          "Adult Price PP": "-",
          "Child Price PP": "Total:",
          Total: lines
            .reduce((t, item) => Number(t) + Number(item.Total), 0)
            .toFixed(2),
        },
      ];
    },

    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    sourceTableHeaders() {
      return this.headers.filter(
        (h) => !h.editable && this.visibleColumns.includes(h.name)
      );
    },
    editableTableHeaders() {
      return this.headers.filter(
        (h) => h.editable && this.visibleColumns.includes(h.name)
      );
    },
    items() {
      //return this.itineraryItems.sort(sortBy("orderNo")).map((itiItem) => {
      // https://tracker.globetrack.ie/issues/7144
      // sort moved to after .map()
      const items = this.itineraryItems
        .map((itiItem) => {
          //merge itinerary items with scenario items
          const scenarioItem = this.scenarioItems.find(
            (item) => itiItem.id == item.itineraryItemId
          );
          const adults = scenarioItem ? scenarioItem.adults : 0;
          const children = scenarioItem ? scenarioItem.children : 0;
          const infants = scenarioItem ? scenarioItem.infants : 0;
          const pax = Number(itiItem.adults) + Number(itiItem.children);
          console.log("infants", infants);
          const getPricePerPaxType = this.perPaxTypePrice(itiItem.pricing);
          const adultsCircuitDiscount =
            Number(itiItem.adults) > 0
              ? itiItem.pricing.circuitDiscountSell / itiItem.adults
              : 0;
          const childrenCircuitDiscount =
            Number(itiItem.children) > 0
              ? itiItem.pricing.circuitDiscountSell / itiItem.children
              : 0;
          const infantsCircuitDiscount =
            Number(itiItem.infants) > 0
              ? itiItem.pricing.circuitDiscountSell / itiItem.infants
              : 0;
          const adultSell = itiItem.pricing.itemBased
            ? (Number(itiItem.price) / pax).toFixed(4)
            : getPricePerPaxType("adult")(itiItem.adults) -
              adultsCircuitDiscount;
          const childSell = itiItem.pricing.itemBased
            ? (Number(itiItem.price) / pax).toFixed(4)
            : getPricePerPaxType("child")(itiItem.children) -
              childrenCircuitDiscount;
          const infantSell = itiItem.pricing.itemBased
            ? 0
            : getPricePerPaxType("infant")(itiItem.infants) -
              infantsCircuitDiscount;
          //console.log(itiItem.serviceName, adultSell, childSell, infantSell);

          return {
            id: itiItem.id,
            orderNo: itiItem.orderNo,
            ratePlan: itiItem.pricing ? itiItem.pricing.ratePlan : "",
            serviceId: itiItem.serviceId,
            startDate: itiItem.startDate,
            supplierName: itiItem.supplierName,
            serviceName: itiItem.serviceName,
            extras: [...itiItem.extras.map(this.mapExtras(itiItem))],
            adultSell,
            childSell,
            infantSell,
            itiAdults: itiItem.adults,
            itiChildren: itiItem.children,
            itiInfants: itiItem.infants,
            adults,
            children,
            infants,
            isIncluded: itiItem.isIncluded,
            isOptional: itiItem.isOptional,
            remainingAdults: this.summary[itiItem.id]
              ? this.summary[itiItem.id].remainingAdults
              : adults,
            remainingChildren: this.summary[itiItem.id]
              ? this.summary[itiItem.id].remainingChildren
              : children,
            remainingInfants: this.summary[itiItem.id]
              ? this.summary[itiItem.id].remainingInfants
              : infants,
            adultsPrice: Number(adultSell * adults).toFixed(4),
            childrenPrice: Number(childSell * children).toFixed(4),
            infantsPrice: Number(infantSell * infants).toFixed(4),
          };
        })
        .sort(sortBy(this.currentSort));
      //console.log(this.currentSort, this.sortDesc, items);
      return this.sortDesc ? items.reverse() : items;
    },
    totalPrice() {
      const servicesAndExtras = this.items
        .filter((i) => i.isIncluded)
        .reduce(
          (a, item) => [...a, item, ...item.extras.filter((e) => e.isIncluded)],
          []
        );
      const t = servicesAndExtras
        .reduce(
          (t, item) =>
            Number(t) +
            Number(item.adultsPrice) +
            Number(item.childrenPrice) +
            Number(item.infantsPrice),
          0
        )
        .toFixed(2);
      //this.scenario.totalPrice = t;

      return t;
    },
  },

  methods: {
    onSortByUpdate(payload) {
      if (payload.length > 0) {
        this.currentSort = payload[0];
      } else {
        this.currentSort = "orderNo";
      }
    },
    onSortDirUpdate(payload) {
      if (payload.length > 0) {
        this.sortDesc = payload[0];
      } else {
        this.sortDesc = false;
      }
    },
    itemToCSV(item) {
      return {
        Date: this.$options.filters.formatDate(item.startDate),
        Supplier: item.supplierName,
        Service: item.serviceName,
        Adults: item.adults,
        Children: item.children,
        "Adult Price PP": item.adultSell,
        "Child Price PP": item.childSell,
        Total:
          Number(item.adults) * Number(item.adultSell) +
          Number(item.children) * Number(item.childSell),
      };
    },
    onShowPaxUpdater() {
      this.showPaxUpdater = true;
    },
    onPaxUpdate(payload) {
      console.log("onPaxUpdate", payload);
      this.showPaxUpdater = false;
      //update accommodation items first
      for (const as of payload.accommodationSettings.filter((s) => s.qty > 0)) {
        //find room by type
        const itiRooms = this.itineraryItems.filter(
          (itiItem) =>
            itiItem.serviceTypeName == "ACCOMMODATION" &&
            itiItem.service.typeName == as.type
        );
        //update rooms
        for (const itiRoom of itiRooms) {
          const scenarioItem = this.scenarioItems.find(
            (item) => itiRoom.id == item.itineraryItemId
          );
          if (scenarioItem) {
            scenarioItem.adults = as.adults;
            scenarioItem.children = as.children;
            scenarioItem.infants = as.infants;
          } else {
            //need to add scenario item
            this.scenarioItems.push({
              itineraryItemId: itiRoom.id,
              adults: Number(as.adults),
              children: Number(as.children),
              infants: Number(as.infants),
            });
          }
          //update extras for this room
          if (itiRoom.extras && itiRoom.extras.length > 0) {
            for (const extra of itiRoom.extras) {
              const scenarioExtra = this.scenarioItems.find(
                (item) => extra.id == item.itineraryItemId
              );
              if (scenarioExtra) {
                scenarioExtra.adults = as.adults;
                scenarioExtra.children = as.children;
                scenarioExtra.infants = as.infants;
              } else {
                //need to add scenario extra
                this.scenarioItems.push({
                  itineraryItemId: extra.id,
                  adults: Number(as.adults),
                  children: Number(as.children),
                  infants: Number(as.infants),
                });
              }
            }
          }
        }
      }
      //update other
      for (const os of payload.otherTypesSettings.filter(
        (s) => Number(s.adults) + Number(s.children) + Number(s.infants) > 0
      )) {
        const itiOthers = this.itineraryItems.filter(
          (itiItem) => itiItem.serviceTypeName == os.type
        );
        for (const itiOther of itiOthers) {
          const scenarioItem = this.scenarioItems.find(
            (item) => itiOther.id == item.itineraryItemId
          );
          if (scenarioItem) {
            scenarioItem.adults = os.adults;
            scenarioItem.children = os.children;
            scenarioItem.infants = os.infants;
          } else {
            //need to add scenario item
            this.scenarioItems.push({
              itineraryItemId: itiOther.id,
              adults: Number(os.adults),
              children: Number(os.children),
              infants: Number(os.infants),
            });
            //update extras for this other
            if (itiOther.extras && itiOther.extras.length > 0) {
              for (const extra of itiOther.extras) {
                const scenarioExtra = this.scenarioItems.find(
                  (item) => extra.id == item.itineraryItemId
                );
                if (scenarioExtra) {
                  scenarioExtra.adults = os.adults;
                  scenarioExtra.children = os.children;
                  scenarioExtra.infants = os.infants;
                } else {
                  //need to add scenario extra
                  this.scenarioItems.push({
                    itineraryItemId: extra.id,
                    adults: Number(os.adults),
                    children: Number(os.children),
                    infants: Number(os.infants),
                  });
                }
              }
            }
          }
        }
      }
      this.emitChange();
    },
    onAddParticipants(payload) {
      this.participants = [...payload];
      this.emitChange();
    },
    onAddParticipant(participant) {
      this.participants = [
        ...this.participants.filter((p) => p.id != participant.id),
        participant,
      ];
      this.emitChange();
    },
    onRemoveParticipant(participant) {
      this.participants = [
        ...this.participants.filter((p) => p.id != participant.id),
      ];
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", {
        id: this.scenario.id,
        name: this.name,
        description: this.description,
        items: [...this.scenarioItems],
        participants: [...this.participants],
        totalPrice: this.totalPrice,
      });
    },
    expandExtras(item) {
      const isExpanded = this.expanded.find((i) => i.id == item.id);
      this.expanded = isExpanded
        ? [...this.expanded.filter((i) => i.id != item.id)]
        : [...this.expanded, item];
    },
    dispatchChange() {
      this.$emit("change", {
        id: this.scenario.id,
        name: this.name,
        description: this.description,
        items: [...this.scenarioItems],
        totalPrice: this.totalPrice,
      });
    },
    perPaxTypePrice(pricing) {
      return (paxType) => (paxNo) =>
        pricing.pricingDays
          ? pricing.pricingDays
              .reduce(
                (total, d) => (total += Number(d[`${paxType}Sell`])), // * Number(paxNo)),
                0
              )
              .toFixed(2)
          : Number(pricing[`${paxType}Sell`]);
    },
    onPaxChange(item) {
      const editedIndex = this.scenarioItems
        .map((s) => s.itineraryItemId)
        .indexOf(item.id);
      if (editedIndex > -1) {
        Object.assign(this.scenarioItems[editedIndex], {
          itineraryItemId: item.id,
          adults: parseInt(item.adults),
          children: parseInt(item.children),
          infants: parseInt(item.infants),
        });
        this.dispatchChange();
      } else {
        this.scenarioItems.push({
          itineraryItemId: item.id,
          adults: Number(item.adults),
          children: Number(item.children),
          infants: Number(item.infants),
        });
      }
    },
    onRemove() {
      this.$emit("remove", { ...this.scenario });
    },
    mapExtras(item) {
      return (itiItemExtra) => {
        const scenarioItem = this.scenarioItems.find(
          (item) => itiItemExtra.id == item.itineraryItemId
        );
        const extra = {
          ...itiItemExtra,
          supplierName: item.supplierName,
          serviceName: itiItemExtra.name,
          ratePlan: itiItemExtra.rateTypeCode,
          itiAdults: itiItemExtra.adults,
          adults: scenarioItem ? scenarioItem.adults : 0,
          itiChildren: itiItemExtra.children,
          children: scenarioItem ? scenarioItem.children : 0,
          itiInfants: itiItemExtra.infants,
          infants: scenarioItem ? scenarioItem.infants : 0,
          adultSell: 0,
          childSell: 0,
          infantSell: 0,
          adultsPrice: 0,
          childrenPrice: 0,
          infantsPrice: 0,
        };

        if (extra.isChildOnly) {
          extra.itiChildren = itiItemExtra.children;
          extra.childSell = Number(
            extra.totalSell / itiItemExtra.children
          ).toFixed(2);
        } else if (extra.isInfantOnly) {
          extra.itiInfants = itiItemExtra.infants;
          extra.infantSell = Number(
            extra.totalSell / itiItemExtra.infants
          ).toFixed(2);
        } else {
          extra.itiAdults = itiItemExtra.adults;
          extra.adultSell = Number(
            extra.totalSell / itiItemExtra.adults
          ).toFixed(2);
        }
        //per item rates use main service pax
        if (extra.rateBase == "I") {
          extra.itiAdults = item.adults;
          extra.itiChildren = item.children;
          extra.itiInfants = item.infants;

          const ppPrice = Number(
            extra.totalSell /
              (Number(item.adults) +
                Number(item.children) +
                Number(item.infants))
          ).toFixed(2);
          extra.adultSell = ppPrice;
          extra.childSell = ppPrice;
        }

        extra.adultsPrice = Number(extra.adultSell * extra.adults).toFixed(2);
        extra.childrenPrice = Number(extra.childSell * extra.children).toFixed(
          2
        );
        //console.log("extra", extra, itiItemExtra, extra.rateBase);
        extra.remainingAdults = this.summary[extra.id]
          ? this.summary[extra.id].remainingAdults
          : extra.adults;
        extra.remainingChildren = this.summary[extra.id]
          ? this.summary[extra.id].remainingChildren
          : extra.children;

        return extra;
      };
    },
    getData() {
      return {
        id: this.scenario.id,
        name: this.name,
        description: this.description,
        items: this.items,
        participants: [...this.participants],
        csvData: this.csvData,
      };
    },
  },
  created() {
    this.expanded = this.items.filter((item) => item.extras.length > 0);
  },
  mounted() {
    this.dispatchChange();
  },
};
</script>

<style>
.scenario-table td,
.services-table td {
  height: 75px !important;
}
.services-table {
  table-layout: fixed;
}
.services-table td {
  box-sizing: border-box;
}
.short-input {
  width: 70px !important;
}
.services-table tr td {
  box-sizing: border-box;
}
.services-table tr td:first-child:not(:only-child) {
  width: 110px !important;
}

.services-table tr td:nth-child(2),
.services-table tr td:nth-child(3) {
  width: 150px !important;
  min-width: 150px !important;
  max-width: 150px !important;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.services-table tr td:nth-child(4),
.services-table tr td:nth-child(5) {
  width: 80px !important;
  min-width: 80px !important;
  max-width: 80px !important;
}
.services-table tr td:nth-child(6),
.services-table tr td:nth-child(7),
.services-table tr td:nth-child(8),
.services-table tr td:nth-child(9),
.services-table tr td:nth-child(10),
.services-table tr th:nth-child(6),
.services-table tr th:nth-child(7),
.services-table tr th:nth-child(8),
.services-table tr th:nth-child(9),
.services-table tr th:nth-child(10) {
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.scenario-table td {
  box-sizing: border-box;
}
.scenario-table tr td:first-child:not(:only-child) {
  width: 85px !important;
  max-width: 85px !important;
  min-width: 85px !important;
}
.scenario-table tr td:nth-child(2),
.scenario-table tr td:nth-child(3),
.scenario-table tr td:nth-child(4) {
  width: 85px !important;
  min-width: 85px !important;
  max-width: 85px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/scenarios/Scenario.vue